import React from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormLabel,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { instance } from 'api/api';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';
import dayjs from 'dayjs';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useEntityIssues, EntityIssuesType } from 'hooks/useEntityIssues';
import InconsistenciesWrapper from './InconsistenciesWrapper';
import { setIssuesTarget, setTargetQuestionId, show } from 'store/conciergeSlice';
import { useDispatch } from 'react-redux';
import IconMi from 'components/common/icon/IconMi';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { RawFormElementProps } from 'components/common/form';
import useEverTrue from 'hooks/useEverTrue';
import { Stack } from '@mui/system';

type KgStatus = 'not_in_graph' | 'profiling' | 'review_required' | 'available' | 'removed';
const KG_STATUSES: KgStatus[] = ['not_in_graph', 'profiling', 'review_required', 'available', 'removed'];
const isKgStatus = (v: unknown): v is KgStatus => KG_STATUSES.includes(v as KgStatus);
type KgAction = 'add' | 'reprofile' | 'remove' | 'make_available';
// 'element' = external report surface: issues fetched via the element-keyed API (MI-29708)
type KgEntityType = 'dataset' | 'domain' | 'element';

export interface ConciergeKgControlProps extends FormControlProps {
    entityType?: KgEntityType;
    entityId: number;
    nameField: string;
    // domain only: routing toggle field watched to lead every state helper with the routing line
    enabledField?: string;
    profiledTimeField: string;
    actionUrl: string;
    // null until the entity has been admitted to the graph and has a node to link to
    knowledgeGraphUrl: string | null;
    /** Translation bundle; the report editor passes a model-worded bundle (MI-29708). */
    translationNS?: string;
    /** Dataset-only Concierge fields (description/instructions) in the available state. */
    showConciergeFields?: boolean;
    /** Actions rendered disabled — e.g. add/reprofile on reports until the profiling engine lands. */
    disabledActions?: KgAction[];
}

interface TransitionResponse {
    concierge_kg_status: KgStatus;
    concierge_kg_profiled_time: string | null;
}

const TRANSLATION_NS: Record<KgEntityType, string> = {
    dataset: 'components/common/concierge-kg-control',
    domain: 'components/common/concierge-kg-control-domain',
    element: 'components/common/concierge-kg-control-report',
};

export default function ConciergeKgControl({ controlProps }: FormElementControlPropsType<ConciergeKgControlProps>) {
    const entityType: KgEntityType = controlProps.entityType ?? 'dataset';
    const isDomain = entityType === 'domain';
    const { t } = useBundleTranslation([controlProps.translationNS ?? TRANSLATION_NS[entityType]]);
    const dispatch = useDispatch();
    const showConciergeFields = controlProps.showConciergeFields ?? true;
    const isActionDisabled = (action: KgAction) => (controlProps.disabledActions ?? []).includes(action);

    const rawStatus = controlProps.form.hookFormWatch(controlProps.name) ?? controlProps.value;
    const status: KgStatus = isKgStatus(rawStatus) ? rawStatus : 'not_in_graph';
    const entityName = controlProps.form.hookFormWatch(controlProps.nameField) as string;
    const profiledTime = controlProps.form.hookFormWatch(controlProps.profiledTimeField) as string | null;
    // Callback subscription via the form's own watch (FormDebugWindow pattern): render-time
    // hookFormWatch(name) can't re-render this memoized element when the Info-tab toggle flips
    const enabledField = controlProps.enabledField;
    const [enabledInd, setEnabledInd] = React.useState<string | undefined>(() =>
        enabledField ? (controlProps.form.hookFormGetValues(enabledField) as string) : undefined,
    );
    React.useEffect(() => {
        if (!enabledField) {
            return;
        }
        setEnabledInd(controlProps.form.hookFormGetValues(enabledField) as string);
        const subscription = controlProps.form.hookFormWatch((values, { name }) => {
            if (!name || name === enabledField) {
                setEnabledInd(values[enabledField] as string);
            }
        });
        return () => subscription.unsubscribe();
    }, [enabledField]);

    const issues = useEntityIssues(controlProps.entityId, entityType as EntityIssuesType, true);
    // Once items have shown, the wrapper owns the section (incl. its resolved/all-clear states) —
    // never fall back to the empty-state placeholder mid-session
    const hasOpenItems = useEverTrue(
        Boolean(
            issues.context &&
            (issues.context.inconsistencies.length > 0 || issues.context.clarifyingQuestions.length > 0),
        ),
    );

    const tParams = { dataset: entityName, name: entityName };

    const mutation = useMutation({
        mutationFn: (action: KgAction) =>
            instance.post<{ status: string; data: TransitionResponse }>(`${controlProps.actionUrl}/${action}`),
        onSuccess: (response) => {
            const { concierge_kg_status, concierge_kg_profiled_time } = response.data.data;
            controlProps.form.hookFormSetValue(controlProps.name, concierge_kg_status, { shouldDirty: false });
            controlProps.form.hookFormSetValue(controlProps.profiledTimeField, concierge_kg_profiled_time, {
                shouldDirty: false,
            });
            if (concierge_kg_status === 'available') {
                toast('success', t('toast_available', tParams));
            }
        },
        onError: () => {
            toast('error', t('action_failed'));
        },
    });

    const isPending = mutation.isPending;
    const handleAction = (action: KgAction) => mutation.mutate(action);

    const pillConfig: Record<
        KgStatus,
        { label: string; color: 'default' | 'primary' | 'warning' | 'success' | 'error'; icon: React.ReactNode }
    > = {
        not_in_graph: {
            label: t('pill_not_in_graph', tParams),
            color: 'default',
            icon: <IconMi icon="catalog" />,
        },
        profiling: {
            label: t('pill_profiling', tParams),
            color: 'primary',
            icon: <CircularProgress size={12} color="inherit" />,
        },
        review_required: {
            label: t('pill_review_required', tParams),
            color: 'warning',
            icon: <IconMi icon="alert" />,
        },
        available: {
            label: t('pill_available', tParams),
            color: 'success',
            icon: <IconMi icon="check" />,
        },
        removed: {
            label: t('pill_removed', tParams),
            color: 'error',
            icon: <IconMi icon="catalog" />,
        },
    };

    const profiledDate = profiledTime ? dayjs(profiledTime).format('MMM D, YYYY') : null;

    const handleOpenConcierge = (questionId?: string) => {
        dispatch(setIssuesTarget({ type: entityType, id: controlProps.entityId }));
        dispatch(setTargetQuestionId(questionId ?? null));
        dispatch(show());
    };

    const viewInKgLink = (
        <Tooltip title={controlProps.knowledgeGraphUrl ? undefined : t('view_in_kg_no_node')}>
            <Box sx={{ display: 'inline-flex' }}>
                <Button
                    variant={'outlined'}
                    component={'a'}
                    href={controlProps.knowledgeGraphUrl ?? '#'}
                    target={'_blank'}
                    disabled={!controlProps.knowledgeGraphUrl}
                    data-test="concierge_kg_link_view"
                >
                    {t('btn_view_in_kg')}
                </Button>
            </Box>
        </Tooltip>
    );

    const reprofileLink = (
        <Button
            startIcon={<IconMi icon="refresh-cw" />}
            variant={'outlined'}
            disabled={isActionDisabled('reprofile')}
            data-test="concierge_kg_reprofile"
            onClick={() => {
                if (!isActionDisabled('reprofile')) {
                    handleAction('reprofile');
                }
            }}
        >
            {t('btn_reprofile')}
        </Button>
    );

    // Every domain state helper leads with the routing consequence of the Info-tab toggle (spec MI-29709 §4.1)
    const domainHelper = isDomain
        ? `${t(enabledInd === 'N' ? 'routing_line_disabled' : 'routing_line_enabled')} ${t(`state_line_${status}`)}`
        : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
            <LoadingPlaceholder open={isPending} />
            <Stack spacing={1}>
                {status === 'review_required' ? (
                    <Alert icon={<IconMi icon="alert" />} severity={'warning'} variant={'accent'}>
                        {t('pill_review_required', tParams)}
                    </Alert>
                ) : (
                    <Box>
                        <Chip
                            label={pillConfig[status].label}
                            color={pillConfig[status].color}
                            icon={pillConfig[status].icon as React.ReactElement}
                        />
                    </Box>
                )}

                {domainHelper && (
                    <Box
                        sx={{
                            color: 'text.secondary',
                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                        }}
                        data-test="concierge_kg_helper"
                    >
                        {domainHelper}
                    </Box>
                )}

                {status === 'not_in_graph' && (
                    <>
                        {!isDomain && (
                            <Box
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                                }}
                            >
                                {t('helper_not_in_graph')}
                            </Box>
                        )}
                        <Box sx={{ pt: 1 }}>
                            <Button
                                startIcon={<IconMi icon="new" />}
                                onClick={() => handleAction('add')}
                                disabled={isPending || isActionDisabled('add')}
                                data-test="concierge_kg_btn_add"
                                variant="outlined"
                            >
                                {t('btn_add')}
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>

            {status === 'profiling' && (
                <>
                    <Alert severity={'warning'} variant={'accent'}>
                        {t('note_profiling', tParams)}
                    </Alert>
                    <LinearProgress data-test="concierge_kg_progress_bar" />
                </>
            )}

            {status === 'review_required' && (
                <>
                    <Alert icon={<IconMi icon={'alert'} />} severity={'warning'} variant={'accent'}>
                        {t('warning_review_required', tParams)}
                    </Alert>
                    {viewInKgLink}
                    <InconsistenciesWrapper
                        issues={issues}
                        onOpenConcierge={handleOpenConcierge}
                        objectType={entityType === 'element' ? 'report' : entityType}
                    />

                    {!hasOpenItems && (
                        <Alert icon={<IconMi icon={'alert'} />} severity={'warning'} variant={'accent'}>
                            <AlertTitle>
                                <Box component={'strong'}>{t('to_resolve_title')}</Box>
                            </AlertTitle>
                            {t('to_resolve_placeholder')}
                        </Alert>
                    )}
                </>
            )}

            {status === 'available' && (
                <>
                    {profiledDate && (
                        <Box sx={{ color: 'text.secondary' }}>
                            {t('profile_summary_date_only', { date: profiledDate })}
                        </Box>
                    )}
                    {!isDomain && showConciergeFields && (
                        <>
                            <FormControl>
                                <FormLabel>{t('description_for_concierge')}</FormLabel>
                                <TemplateFormComponent
                                    elementProps={{ form: controlProps.form } as RawFormElementProps}
                                    component={'FormTextarea'}
                                    name={'description_for_concierge'}
                                    props={{ rows: 4 }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>{t('concierge_instructions')}</FormLabel>
                                <TemplateFormComponent
                                    elementProps={{ form: controlProps.form } as RawFormElementProps}
                                    component={'FormTextarea'}
                                    name={'concierge_instructions'}
                                    props={{ rows: 4 }}
                                />
                            </FormControl>
                        </>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {viewInKgLink}
                        {reprofileLink}

                        <Button
                            startIcon={<IconMi icon="times" />}
                            variant={'outlined'}
                            color="error"
                            data-test="concierge_kg_remove"
                            onClick={() => handleAction('remove')}
                        >
                            {t('btn_remove')}
                        </Button>
                    </Box>
                </>
            )}

            {status === 'removed' && (
                <>
                    {!isDomain && (
                        <Alert icon={<IconMi icon="alert" />} severity={'warning'} variant={'accent'}>
                            {t('helper_removed', tParams)}
                        </Alert>
                    )}
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<IconMi icon="check" />}
                            onClick={() => handleAction('make_available')}
                            disabled={isPending}
                            data-test="concierge_kg_btn_make_available"
                        >
                            {t('btn_make_available')}
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {viewInKgLink}
                        {reprofileLink}
                    </Box>
                </>
            )}
        </Box>
    );
}

export class ConciergeKgControlBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeKgControlProps {
        return {
            ...this.controlProps,
            entityType: this.elementProps.componentProps?.entityType,
            entityId: this.elementProps.componentProps?.entityId,
            nameField: this.elementProps.componentProps?.nameField,
            enabledField: this.elementProps.componentProps?.enabledField,
            profiledTimeField: this.elementProps.componentProps?.profiledTimeField,
            actionUrl: this.elementProps.componentProps?.actionUrl,
            knowledgeGraphUrl: this.elementProps.componentProps?.knowledgeGraphUrl ?? null,
            translationNS: this.elementProps.componentProps?.translationNS,
            showConciergeFields: this.elementProps.componentProps?.showConciergeFields,
            disabledActions: this.elementProps.componentProps?.disabledActions,
        };
    }
}
